/**
 * Google Consent Mode v2 bridge for the GTM-enabled apps.
 *
 * The main websites (leanpay.si / leanpay.ro) call `gtag('consent', 'update', ...)` and fire
 * the `analytics_consent_given` event when the user accepts cookies. The apps only wrote the
 * `cookie_control_*` cookies, so analytics never learned that consent was granted (TEC-19136).
 *
 * Consent state does not persist across page loads — it must be re-announced from the stored
 * cookie on every load, which also honors consent already given on the main website (the
 * cookie is set on the apex domain and shared with all subdomains).
 */

const COOKIES_ARRAY_NAME = 'cookie_control_enabled_cookies'
const COOKIES_CONSENT_NAME = 'cookie_control_consent'
const ANALYTICS_CONSENT_EVENT = 'analytics_consent_given'

// Values of `CookieTypes` from @leanpay/models — mirrored here because models depends on
// utils, so importing the enum would be circular.
const ANALYTICAL_COOKIE = 'analytical'
const FUNCTIONAL_COOKIE = 'functional'
const MARKETING_COOKIE = 'marketing'

type ConsentValue = 'granted' | 'denied'

declare global {
    interface Window {
        dataLayer?: unknown[]
    }
}

// Google's consent API only accepts the Arguments object, not a plain array/object.
function gtag(..._args: unknown[]) {
    window.dataLayer = window.dataLayer ?? []
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
}

const readRawCookie = (name: string): string | undefined => {
    const entry = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`))
    return entry?.substring(name.length + 1)
}

/**
 * Pushes the consent selection to Google Consent Mode and fires the same
 * `analytics_consent_given` event the main websites use, so GTM triggers align.
 *
 * @param enabledCookies the accepted `CookieTypes` values (e.g. from the consent dialog
 * or the stored `cookie_control_enabled_cookies` cookie)
 */
export const updateGoogleConsent = (enabledCookies: string[]): void => {
    const has = (type: string): ConsentValue =>
        enabledCookies.includes(type) ? 'granted' : 'denied'

    gtag('consent', 'update', {
        ad_storage: has(MARKETING_COOKIE),
        ad_user_data: has(MARKETING_COOKIE),
        ad_personalization: has(MARKETING_COOKIE),
        analytics_storage: has(ANALYTICAL_COOKIE),
        functionality_storage: has(FUNCTIONAL_COOKIE),
        personalization_storage: has(FUNCTIONAL_COOKIE),
        security_storage: 'granted',
    })

    if (enabledCookies.includes(ANALYTICAL_COOKIE)) {
        window.dataLayer = window.dataLayer ?? []
        window.dataLayer.push({ event: ANALYTICS_CONSENT_EVENT })
    }
}

/**
 * Re-announces consent from the stored cookie. Call once at app startup, after GTM
 * initialization. No-op when the user has not answered the consent banner yet.
 */
export const announceStoredGoogleConsent = (): void => {
    if (readRawCookie(COOKIES_CONSENT_NAME) !== 'true') {
        return
    }
    const enabledCookies = readRawCookie(COOKIES_ARRAY_NAME)?.split(',') ?? []
    updateGoogleConsent(enabledCookies)
}
